import React from 'react';
import {
    Responsive,
    SimpleList,
    Datagrid,
    TextField,
    SimpleForm,
    NumberInput,
    required,
    number,
    minValue
} from 'admin-on-rest';
import List from '../admin/List';
import Create from '../admin/Create';
import Edit from '../admin/Edit';
import EditButton from '../buttons/EditButtonByCustomKey';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/av/library-add';
import {resolveAccess} from "../shared/authFunctions";

export const fuseLoyaltyIncentiveTierIcon = Icon;

/*
 * View: List
 */

// Set title for List Loyalty Incentive Tiers
const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                className="avatar-title"/>Loyalty Incentive Tiers</span>;

const ListDescription = () => {
    return (
        <span>
            <p>Incentive Tiers set earning ratios based on minimum purchase amounts. These allow you to prompt
                customers to earn more Reward Points if they spend greater amounts.</p>
            <p>Incentive Tiers work with Ranks to provide extra points for customers. Ranks and Tiers must exist before
                Loyalty Accounts may be created.</p>
        </span>
    );
};

export const fuseLoyaltyIncentiveTierList = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescription/>} sort={{field: 'min_dollars', order: 'ASC'}}>
        {permissions =>
            <Responsive
                small={
                    <SimpleList
                        primaryText={record => record.min_dollars}
                    />
                }
                medium={
                    <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                        <TextField source="min_dollars" label="Minimum Dollar Amount"/>
                        <TextField source="reward_point_earning_ratio" label="Reward Earning Ratio"/>
                        {resolveAccess(permissions, ['loyalty_all']) &&
                        <EditButton customKey="min_dollars"/>
                        }
                    </Datagrid>
                }
            />
        }
    </List>
);

/*
 * View: Create form
 */

// Set title for Create Loyalty Incentive Tier
const CreateTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>Create a Loyalty Incentive Tier</span>;

const CreateDescription = () => {
    return (
        <span>
            <p>Incentive Tiers set earning ratios based on minimum purchase amounts. These allow you to prompt
                customers to earn more Reward Points for spending more during a single purchase.</p>
            <p>A <strong>Minimum Dollar Amount</strong> is the minimum total spent on a single sale to reach this
                Tier. For example:</p>
            <ul>
                <li>During a sale, a Customer usually earns 1 Reward Point for each dollar spent.</li>
                <li>A Tier is created at 500 with an earning ratio of 1.5.</li>
                <li>If the customer spends over $500, each dollar instead earns 1.5 Reward Points.</li>
                <li>If a customer spends less than the minimum, Points are earned based on the next lowest
                    Incentive Tier (minimum 1).</li>
            </ul>
            <p>Create a Loyalty Incentive Tier by adding a minimum dollar amount and an earning ratio.</p>
            <p><strong>Note:</strong> To utilize Loyalty Accounts, you must create at least one Tier, with
                a <em>Minimum Dollar Amount</em> of 1.</p>
        </span>
    );
};

// Set defaults for Create form
const defaultCreateValues = {min_dollars: 1, reward_point_earning_ratio: 1};

/*
 * Create Loyalty Incentive Tier form
 */
export const fuseLoyaltyIncentiveTierCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm defaultValue={defaultCreateValues} redirect="list">
            <NumberInput source="min_dollars" label="Minimum Dollar Amount"
                         step={1}
                         validate={[required, number, minValue(1, 'Must be at least 1')]}/>
            <span style={viewStyles.fieldDescription}>
                Enter the <strong>Minimum Dollar Amount</strong> for this Incentive Tier.
                <TooltipIcon dataFor="ttMin"/>
                <ReactTooltip id="ttMin" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Minimum Dollar Amount</strong> is the minimum total spent on a single sale to reach
                    this Tier. This value must be at least 1.
                    <br/><br/>
                    For example:
                    <br/><br/>
                    <ul>
                        <li>Each dollar spent during a sale usually equals 1 Reward Point earned.</li>
                        <li>A Tier is created at 500 with an earning ratio of 1.5.</li>
                        <li>If the customer spends over $500, each dollar earns 1.5 Reward Points.</li>
                    </ul>
                </ReactTooltip>
            </span>

            <NumberInput source="reward_point_earning_ratio" label="Reward Point Earning Ratio"
                         step={0.5}
                         validate={[required, number, minValue(0, 'Must be a positive number')]}/>
            <span style={viewStyles.fieldDescription}>
                Enter the <strong>Earning ratio</strong>. This may be a number such as 1, 1.5, 2.75, etc.
                <TooltipIcon dataFor="ttRatio"/>
                <ReactTooltip id="ttRatio" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Reward Point Earning Ratio</strong> affects how many Reward Points are earned during a
                    purchase on the POS System. An earning ratio is multiplied by the dollar amount to determine the
                    total Points earned.
                    <br/><br/>
                    If desired, this value may be 0.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the list of Incentive Tiers.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Create>
);

/*
 * View: Edit form
 */

// Set title for Edit Loyalty Incentive Tier
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit Loyalty Incentive Tier</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>Modify the minimum dollar amount and earning ratio below. Click <strong>Save</strong> to store this
                Loyalty Incentive Tier.</p>
        </span>
    );
};

/*
 * Edit Loyalty Incentive Tier form
 */
export const fuseLoyaltyIncentiveTierEdit = (props) => (
    <Edit {...props} title={<EditTitle/>} desc={<EditDescription/>}>
        <SimpleForm>
            <NumberInput source="min_dollars" label="Minimum Dollar Amount"
                         step={1}
                         validate={[required, number, minValue(1, 'Must be at least 1')]}/>
            <span style={viewStyles.fieldDescription}>
                The <strong>Minimum Dollar Amount</strong> for this Incentive Tier.
                <TooltipIcon dataFor="ttMin"/>
                <ReactTooltip id="ttMin" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Minimum Dollar Amount</strong> is the minimum total spent on a single sale to reach
                    this Tier. This value must be at least 1.
                </ReactTooltip>
            </span>

            <NumberInput source="reward_point_earning_ratio" label="Reward Point Earning Ratio"
                         step={0.5}
                         validate={[required, number, minValue(0, 'Must be a positive number')]}/>
            <span style={viewStyles.fieldDescription}>
                The <strong>Reward Point Earning Ratio</strong> may be a number such as 1, 1.5, 2.75, etc.
                <TooltipIcon dataFor="ttRatio"/>
                <ReactTooltip id="ttRatio" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>Reward Point Earning Ratio</strong> affects how many Reward Points are earned during a
                    purchase on the POS System. An earning ratio is multiplied by the dollar amount to determine the
                    total Points earned.
                    <br/><br/>
                    If desired, this value may be 0.
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the list of Incentive Tiers.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/Loyalty/incentiveTier.js